<template>
  <b-modal
    v-model="internalVisible"
    size="xl"
    modal-class="destination-fullscreen-modal"
    hide-footer
    no-close-on-backdrop
    @hidden="onHidden"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center justify-content-between w-100">
        <h5 class="mb-0">
          <feather-icon icon="MapPinIcon" size="16" class="mr-50" />
          Definir Destino
        </h5>
        <button class="btn btn-sm btn-outline-secondary" @click="close">
          <feather-icon icon="XIcon" size="14" />
        </button>
      </div>
    </template>

    <div class="destination-modal">
      <div class="destination-modal__filters">
        <div class="form-group mb-0">
          <label class="small mb-25 d-block">Tipo de Estabelecimento</label>
          <v-select
            v-model="filters.type_establishment_ids"
            :options="establishmentTypes"
            :reduce="(t) => t.id"
            label="establishment_type_name"
            placeholder="Todos"
            class="vselect"
            multiple
          />
        </div>
        <div class="form-group mb-0">
          <label class="small mb-25 d-block">Tipo de Ocorrência</label>
          <v-select
            v-model="filters.dispatch_type_ids"
            :options="dispatchTypes"
            :reduce="(t) => t.id"
            label="name"
            placeholder="Todos"
            class="vselect"
            multiple
          />
        </div>
        <div class="form-group mb-0">
          <label class="small mb-25 d-block">Motivo</label>
          <v-select
            v-model="filters.dispatch_reason_ids"
            :options="dispatchReasons"
            :reduce="(r) => r.id"
            label="name"
            placeholder="Todos"
            class="vselect"
            multiple
          />
        </div>
        <div class="form-group mb-0">
          <label class="small mb-25 d-block">Linha de Cuidado</label>
          <v-select
            v-model="filters.care_line_ids"
            :options="careLines"
            :reduce="(c) => c.id"
            label="name"
            placeholder="Todas"
            class="vselect"
            multiple
          />
        </div>
        <div class="form-group mb-0 destination-modal__search">
          <label class="small mb-25 d-block">Buscar</label>
          <input
            v-model="search"
            type="text"
            class="form-control"
            placeholder="Nome do estabelecimento..."
          >
        </div>
      </div>

      <div class="form-group destination-modal__observation">
        <label class="small mb-25 d-block">
          Observação (opcional — aplicada ao destino selecionado abaixo)
        </label>
        <input
          v-model="observation"
          type="text"
          class="form-control"
          maxlength="2000"
          placeholder="Observação..."
        >
      </div>

      <div class="destination-modal__body">
        <div class="destination-modal__list">
          <div v-if="loading" class="text-center py-4">
            <b-spinner variant="success" />
          </div>
          <div v-else-if="!list.length" class="text-center text-muted py-4">
            Nenhum estabelecimento encontrado com os filtros atuais.
          </div>
          <template v-else>
            <div class="destination-modal__cards">
              <div
                v-for="e in list"
                :key="e.id"
                class="destination-card"
                :class="{ 'destination-card--hover': hoveredId === e.id }"
                @mouseenter="hoveredId = e.id"
                @mouseleave="hoveredId = null"
              >
                <div class="destination-card__main">
                  <div class="destination-card__name">
                    {{ e.establishment_name }}
                    <span
                      v-if="e.type_establishment"
                      class="badge badge-light-secondary ml-50"
                    >{{ e.type_establishment.establishment_type_name }}</span>
                  </div>
                  <div class="destination-card__address">
                    {{ formatAddress(e) }}
                  </div>
                  <div v-if="e.distance_km !== undefined" class="destination-card__distance">
                    <feather-icon icon="NavigationIcon" size="11" class="mr-25" />
                    {{ e.distance_km }} km da ocorrência
                  </div>
                </div>
                <button
                  class="btn btn-sm btn-success"
                  :disabled="saving"
                  @click="chooseEstablishment(e)"
                >
                  <b-spinner
                    v-if="saving && choosingId === e.id"
                    small
                    class="mr-25"
                  />
                  <feather-icon
                    v-else
                    icon="CheckCircleIcon"
                    size="13"
                    class="mr-25"
                  />
                  Selecionar
                </button>
              </div>
            </div>

            <div
              v-if="pagination.last_page > 1"
              class="d-flex justify-content-center p-1"
            >
              <pagination
                v-model="pagination.current_page"
                :options="opcoesPaginacao"
                :records="pagination.total"
                :per-page="pagination.per_page"
                @paginate="loadList"
              />
            </div>
          </template>
        </div>

        <div class="destination-modal__map">
          <GmapMap
            ref="mapRef"
            :center="mapCenter"
            :zoom="13"
            map-type-id="roadmap"
            style="width: 100%; height: 100%"
          >
            <GmapMarker
              v-if="hasOrigin"
              :position="originPosition"
              :icon="originIcon"
              title="Ocorrência"
            />
            <GmapMarker
              v-for="pin in mapPins"
              :key="'est-' + pin.id"
              :position="{ lat: parseFloat(pin.latitude), lng: parseFloat(pin.longitude) }"
              :icon="pinIcon(pin)"
              :title="pin.establishment_name"
              @click="focusEstablishment(pin)"
            />
          </GmapMap>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import vSelect from 'vue-select'
import Pagination from 'vue-pagination-2'
import opcoesPaginacao from '@/libs/paginationOptions'

export default {
  name: 'DefineDestinationModal',
  components: { vSelect, Pagination },
  props: {
    value: { type: Boolean, default: false },
    occurrenceInvolvedId: { type: [Number, String], default: null },
    originLat: { type: [Number, String], default: null },
    originLng: { type: [Number, String], default: null },
  },
  data() {
    return {
      filters: {
        type_establishment_ids: [],
        dispatch_type_ids: [],
        dispatch_reason_ids: [],
        care_line_ids: [],
      },
      search: '',
      // Incrementado a cada loadList() — usado pra descartar respostas de
      // requisições antigas que chegam depois de uma mais recente (ex:
      // abrir o modal dispara várias buscas de uma vez, e sem isso a
      // resposta "sem filtro" podia sobrescrever a resposta já filtrada).
      listRequestId: 0,
      establishmentTypes: [],
      dispatchTypes: [],
      dispatchReasons: [],
      careLines: [],
      list: [],
      mapPins: [],
      pagination: {
        current_page: 1, last_page: 1, total: 0, per_page: 10,
      },
      loading: false,
      saving: false,
      choosingId: null,
      hoveredId: null,
      observation: '',
      mapCenter: { lat: -15.77972, lng: -47.92972 },
      searchDebounceTimer: null,
      // Marcador vermelho pro ponto de origem (ocorrência) — mesmo estilo do
      // pin de ocorrência em Occurrence/Index.vue.
      originIcon: {
        url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
        scaledSize: { width: 44, height: 44 },
      },
      candidateIcon: {
        url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
        scaledSize: { width: 36, height: 36 },
      },
    }
  },
  computed: {
    opcoesPaginacao() {
      return opcoesPaginacao
    },
    internalVisible: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },
    hasOrigin() {
      return this.originLat !== null && this.originLat !== undefined
        && this.originLng !== null && this.originLng !== undefined
    },
    originPosition() {
      return { lat: parseFloat(this.originLat), lng: parseFloat(this.originLng) }
    },
  },
  watch: {
    value(visible) {
      if (visible) this.onOpen()
    },
    filters: {
      deep: true,
      handler() { this.loadList(1) },
    },
    search() {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => this.loadList(1), 400)
    },
  },
  methods: {
    onOpen() {
      this.resetFilters()
      this.mapCenter = this.hasOrigin ? this.originPosition : this.mapCenter
      this.loadFilterOptions()
      this.loadList(1)
    },
    onHidden() {
      this.$emit('input', false)
    },
    resetFilters() {
      this.filters = {
        type_establishment_ids: [],
        dispatch_type_ids: [],
        dispatch_reason_ids: [],
        care_line_ids: [],
      }
      this.search = ''
      this.observation = ''
      this.hoveredId = null
    },
    formatAddress(establishment) {
      const parts = []
      let street = establishment.address || ''
      if (establishment.number) street += `, ${establishment.number}`
      if (street) parts.push(street)
      if (establishment.complement) parts.push(establishment.complement)
      if (establishment.neighborhood) parts.push(establishment.neighborhood)

      const city = establishment.city ? establishment.city.name : ''
      const state = establishment.state ? establishment.state.code : ''
      const cityState = [city, state].filter(Boolean).join('/')
      if (cityState) parts.push(cityState)

      return parts.join(' - ') || '—'
    },
    // Nó operacional atual: $store.state.selected_node (setado pelo
    // NodeSelector), nunca o current_node_id do backend — esse costuma
    // ficar null pra vários usuários e faz os for-node voltarem vazios.
    currentNodeId() {
      return this.$store.state.selected_node ? this.$store.state.selected_node.id : null
    },
    loadFilterOptions() {
      axios
        .get(`${this.$store.state.api}establishment_types?paginated=false&status=1`)
        .then(r => { this.establishmentTypes = r.data.data || [] })
      axios
        .get(`${this.$store.state.api}dispatch_types/for-node`, { params: { node_id: this.currentNodeId() } })
        .then(r => { this.dispatchTypes = r.data.data || [] })
      axios
        .get(`${this.$store.state.api}care_lines/for-node`, { params: { node_id: this.currentNodeId() } })
        .then(r => { this.careLines = r.data.data || [] })
      // Motivo não é mais filtrado em cascata pelo Tipo — com multi-seleção
      // nos dois filtros ao mesmo tempo, a cascata complicaria mais do que
      // ajudaria. Carrega todos os motivos do nó de uma vez só.
      axios
        .get(`${this.$store.state.api}dispatch_reasons/for-node`, { params: { node_id: this.currentNodeId() } })
        .then(r => { this.dispatchReasons = r.data.data || [] })
    },
    loadList(page = 1) {
      this.loading = true
      // Só a resposta da requisição mais recente pode atualizar a lista —
      // evita que uma busca antiga (ex: a sem-filtro disparada ao abrir o
      // modal) chegue depois e sobrescreva um resultado já filtrado.
      this.listRequestId += 1
      const requestId = this.listRequestId
      const params = {
        page,
        per_page: this.pagination.per_page,
        ...this.filters,
        search: this.search,
      }
      if (this.hasOrigin) {
        params.lat = this.originLat
        params.lng = this.originLng
      }
      return axios
        .get(`${this.$store.state.api}establishments/for-destination`, { params })
        .then(r => {
          if (requestId !== this.listRequestId) return
          this.list = r.data.data || []
          this.mapPins = r.data.map_pins || []
          this.pagination = { ...this.pagination, ...r.data.meta }
        })
        .catch(() => {
          if (requestId !== this.listRequestId) return
          this.$message('Erro', 'Não foi possível carregar os estabelecimentos.', 'error')
        })
        .finally(() => {
          if (requestId === this.listRequestId) this.loading = false
        })
    },
    pinIcon(pin) {
      return pin.id === this.hoveredId
        ? { ...this.candidateIcon, url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' }
        : this.candidateIcon
    },
    focusEstablishment(pin) {
      this.hoveredId = pin.id
      this.mapCenter = { lat: parseFloat(pin.latitude), lng: parseFloat(pin.longitude) }
    },
    async chooseEstablishment(establishment) {
      if (!this.occurrenceInvolvedId) return
      this.saving = true
      this.choosingId = establishment.id
      try {
        const res = await axios.post(`${this.$store.state.api}occurrence_involved_destinations`, {
          occurrence_involved_id: this.occurrenceInvolvedId,
          establishment_id: establishment.id,
          observation: this.observation || null,
        })
        const saved = res.data.data || res.data
        this.$message('Destino definido', 'Destino salvo com sucesso.', 'success')
        this.$emit('saved', saved)
        this.internalVisible = false
      } catch (e) {
        this.$message('Erro', 'Erro ao definir destino.', 'error')
      } finally {
        this.saving = false
        this.choosingId = null
      }
    },
  },
}
</script>

<style>
/* Modal grande, mas não 100vw/100vh — isso cobria até o menu lateral do
   app. Sobra uma margem pra dar pra ver que ainda dá pra fechar/voltar.
   Precisa ser global (sem `scoped`) pra pegar as classes que o
   bootstrap-vue injeta fora do componente. */
.destination-fullscreen-modal .modal-dialog {
  max-width: 95vw;
  width: 95vw;
  height: 90vh;
  margin: 5vh auto;
}
.destination-fullscreen-modal .modal-content {
  height: 90vh;
  border-radius: 8px;
}
.destination-fullscreen-modal .modal-body {
  height: calc(90vh - 62px);
  overflow: hidden;
  padding: 12px 16px;
  display: flex;
}
</style>

<style scoped>
.destination-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.destination-modal__filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr)) minmax(220px, 1.5fr);
  gap: 12px;
  margin-bottom: 10px;
}

.destination-modal__observation {
  margin-bottom: 10px;
}

.destination-modal__body {
  flex: 1;
  display: flex;
  gap: 14px;
  min-height: 0;
}

.destination-modal__list {
  width: 38%;
  min-width: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.destination-modal__map {
  flex: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0ddf5;
}

.destination-modal__cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.destination-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e0ddf5;
  border-radius: 8px;
  transition: border-color 0.15s, background 0.15s;
}

.destination-card--hover {
  border-color: #28c76f;
  background: #f4fdf8;
}

.destination-card__name {
  font-weight: 600;
  font-size: 0.9rem;
}

.destination-card__address {
  font-size: 0.78rem;
  color: #6e6b7b;
}

.destination-card__distance {
  font-size: 0.75rem;
  color: #28c76f;
  margin-top: 2px;
}
</style>
